import { useState, useEffect } from 'react';
import api from './api/axios';

interface Message {
  content: string;
  role: string;
}

interface UseArgumentProps {
  uid: string;
  userName: string;
}

// Private interface just for parsing the WebSocket message
interface ArgumentStateMessage {
  argument_state: {
    uid: string;
    topic: string;
    state: string;
    user_arguments: Array<{
      user_identifier: string;
      state: string;
    }>;
  };
}

const useArgument = ({ uid, userName }: UseArgumentProps) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [argumentList, setArgumentList] = useState<Message[]>([]);
  const [isFinalized, setIsFinalized] = useState(false);
  const [awaitingJudgment, setAwaitingJudgment] = useState(false);
  const [judgment, setJudgment] = useState('');
  const [argumentSummary, setArgumentSummary] = useState('');
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);
  const [opponentName, setOpponentName] = useState<string | null>(null);
  const [opponentState, setOpponentState] = useState<string | null>(null);

  // WebSocket for argument state updates
  useEffect(() => {
    if (!uid) return;

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) return;

    const wsUrl = baseUrl.replace(/^http/, 'ws') + `/ws/argument-state/${uid}/`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'identify', userName }));
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as ArgumentStateMessage;
        
        if (data.argument_state) {
          const otherUser = data.argument_state.user_arguments.find(
            (u) => u.user_identifier !== userName
          );
          if (otherUser) {
            setOpponentName(otherUser.user_identifier);
            setOpponentState(otherUser.state);
          }
        }
      } catch (err) {
        console.error('[ArgumentState] Parse error:', err);
      }
    };

    socket.onerror = (error) => {
      console.error('[ArgumentState] Error:', error);
    };

    return () => {
      socket.close();
    };
  }, [uid, userName]);

  useEffect(() => {
    if (!isFinalized || !uid) return;

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) return;

    const wsUrl = baseUrl.replace(/^http/, 'ws') + `/ws/judgment/${uid}/`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'identify', userName }));
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.judgment) {
          setJudgment(data.judgment);
          setAwaitingJudgment(false);
        }
      } catch (err) {
        console.error('[Judgment] Parse error:', err);
      }
    };

    return () => {
      socket.close();
    };
  }, [isFinalized, uid, userName]);

  const handleUserInputSubmit = async () => {
    if (!uid || !userName || !userInput.trim()) return;
    setLoading(true);
    setMessage('');
    try {
      const response = await api.post(
        `/add-side-of-argument/${uid}/${userName}/`,
        { argument_text: userInput }
      );

      if (response.data.message_log) {
        setArgumentList(response.data.message_log);
        setHasReceivedResponse(true);
      }
      setUserInput('');
    } catch (err: any) {
      console.error('[Add Argument] Error:', err);
      setMessage(err.response?.data?.error || 'Error adding to argument.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeArgument = async () => {
    setIsFinalized(true);
    setAwaitingJudgment(true);
    setLoading(true);
    setMessage('');
    try {
      const response = await api.post(
        `/finalize-argument/${uid}/${userName}/`,
        {}
      );
      setArgumentSummary(response.data.argument_summary);
    } catch (err: any) {
      console.error('[Finalize] Error:', err);
      setMessage('Error finalizing argument.');
      setIsFinalized(false);
      setAwaitingJudgment(false);
    } finally {
      setLoading(false);
    }
  };

  return {
    message,
    loading,
    userInput,
    setUserInput,
    argumentList,
    argumentSummary,
    isFinalized,
    awaitingJudgment,
    judgment,
    opponentName,
    opponentState,
    hasReceivedResponse,
    handleUserInputSubmit,
    handleFinalizeArgument,
  };
};

export default useArgument;
